import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
    formatTime, calculateTotalSeconds, SOUND_CATEGORIES,
} from '@/lib/timer/constants';
import { AudioManager } from '@/lib/timer/audio';
import { Volume2, Play, Save, FolderOpen, Music, Dumbbell } from 'lucide-react';

const TIME_FIELDS = [
    { key: 'prepare', label: 'Preparación', phase: 0 },
    { key: 'work', label: 'Ejercicio', phase: 1 },
    { key: 'rest', label: 'Descanso', phase: 2 },
    { key: 'restBetween', label: 'Descanso entre tabatas', phase: 3 },
    { key: 'cooldown', label: 'Enfriamiento', phase: 5 },
];

const NUMBER_FIELDS = [
    { key: 'rounds', label: 'Rondas por tabata', min: 1 },
    { key: 'tabatas', label: 'Tabatas', min: 1 },
];

function TimeField({ label, minutes, seconds, color, onMinutesChange, onSecondsChange, onColorChange }) {
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-white/60 mb-1.5">{label}</p>
                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <Label className="sr-only">Minutos</Label>
                        <Input
                            type="number"
                            min={0}
                            max={99}
                            value={minutes}
                            onChange={(e) => onMinutesChange(parseInt(e.target.value, 10) || 0)}
                            className="bg-[#0a0a0a] border-[#3a3a3f] text-white text-center"
                        />
                    </div>
                    <span className="text-white/60 text-sm">:</span>
                    <div className="flex-1">
                        <Label className="sr-only">Segundos</Label>
                        <Input
                            type="number"
                            min={0}
                            max={59}
                            value={seconds}
                            onChange={(e) => onSecondsChange(parseInt(e.target.value, 10) || 0)}
                            className="bg-[#0a0a0a] border-[#3a3a3f] text-white text-center"
                        />
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-center">
                <Label className="sr-only">Color</Label>
                <input
                    type="color"
                    value={color}
                    onChange={(e) => onColorChange(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-0 p-0"
                    title="Color de fase"
                />
            </div>
        </div>
    );
}

export default function TimerSetup({
    config,
    onChange,
    onStart,
    presets,
    onSavePreset,
    onLoadPreset,
    onDeletePreset,
}) {
    const audioRef = useRef(new AudioManager(config.volume));
    const [soundOpen, setSoundOpen] = useState(false);
    const [presetOpen, setPresetOpen] = useState(false);
    const [presetName, setPresetName] = useState('');

    useEffect(() => {
        audioRef.current.setVolume(config.volume);
    }, [config.volume]);

    const update = (key, value) => {
        onChange({ ...config, [key]: value });
    };

    const updateTime = (key, minutes, seconds) => {
        update(key, Math.max(0, minutes * 60 + seconds));
    };

    const updateColor = (key, color) => {
        update('colors', { ...config.colors, [key]: color });
    };

    const updateSound = (key, index) => {
        const value = parseInt(index, 10);
        update('sounds', { ...config.sounds, [key]: value });
        audioRef.current.play(key, value);
    };

    const previewSound = (key) => {
        audioRef.current.play(key, config.sounds[key]);
    };

    const savePreset = () => {
        const name = presetName.trim();
        if (!name) return;
        onSavePreset(name, config);
        setPresetName('');
    };

    const loadPreset = (preset) => {
        onLoadPreset(preset);
        setPresetOpen(false);
    };

    const deletePreset = (idx) => {
        onDeletePreset(idx);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
                        <Dumbbell className="h-6 w-6" />
                        Timer
                    </h1>
                    <p className="text-sm text-white/60 mt-1">Configurá un timer con sonidos para tus clases.</p>
                </div>
                <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-white/60">Tiempo total</p>
                    <p className="text-2xl font-bold text-white">{formatTime(calculateTotalSeconds(config))}</p>
                </div>
            </div>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <CardTitle className="text-white text-sm uppercase tracking-wider">Fases</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {TIME_FIELDS.map((field) => {
                        const totalSeconds = config[field.key];
                        const minutes = Math.floor(totalSeconds / 60);
                        const seconds = totalSeconds % 60;
                        return (
                            <TimeField
                                key={field.key}
                                label={field.label}
                                minutes={minutes}
                                seconds={seconds}
                                color={config.colors[field.key]}
                                onMinutesChange={(m) => updateTime(field.key, m, seconds)}
                                onSecondsChange={(s) => updateTime(field.key, minutes, s)}
                                onColorChange={(c) => updateColor(field.key, c)}
                            />
                        );
                    })}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {NUMBER_FIELDS.map((field) => (
                    <Card key={field.key} className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="text-white text-sm uppercase tracking-wider">{field.label}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Input
                                type="number"
                                min={field.min}
                                value={config[field.key]}
                                onChange={(e) => update(field.key, Math.max(field.min, parseInt(e.target.value, 10) || 0))}
                                className="bg-[#0a0a0a] border-[#3a3a3f] text-white"
                            />
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <CardTitle className="text-white text-sm uppercase tracking-wider">Color extra</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-3">
                        <Label className="text-white/60 text-sm">Tabata</Label>
                        <input
                            type="color"
                            value={config.colors.tabata}
                            onChange={(e) => updateColor('tabata', e.target.value)}
                            className="w-10 h-10 rounded cursor-pointer bg-transparent border-0 p-0"
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => setSoundOpen(true)} className="border-[#3a3a3f] text-white hover:bg-white/5">
                    <Music className="h-4 w-4 mr-2" />
                    Sonidos
                </Button>
                <Button variant="outline" onClick={() => setPresetOpen(true)} className="border-[#3a3a3f] text-white hover:bg-white/5">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Presets
                </Button>
                <Button onClick={onStart} className="ml-auto bg-white text-black hover:bg-white/90">
                    <Play className="h-4 w-4 mr-2" />
                    Iniciar
                </Button>
            </div>

            {/* Sound setup dialog */}
            <Dialog open={soundOpen} onOpenChange={setSoundOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">Configurar sonidos</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        {Object.entries(SOUND_CATEGORIES).map(([key, { label, files }]) => (
                            <div key={key}>
                                <Label className="text-white/60 text-xs uppercase tracking-wider mb-1.5 block">{label}</Label>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={config.sounds[key]}
                                        onChange={(e) => updateSound(key, e.target.value)}
                                        className="flex-1 h-10 px-3 rounded bg-[#0a0a0a] border border-[#3a3a3f] text-white text-sm appearance-none"
                                    >
                                        {files.map((file, i) => (
                                            <option key={file + i} value={i + 1}>
                                                {file === 'nic' ? 'X' : `Sonido ${i + 1}`}
                                            </option>
                                        ))}
                                    </select>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={() => previewSound(key)}
                                        className="border-[#3a3a3f] text-white hover:bg-white/5 shrink-0"
                                        title="Escuchar"
                                    >
                                        <Volume2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <div>
                            <Label className="text-white/60 text-xs uppercase tracking-wider mb-1.5 block flex items-center gap-2">
                                <Volume2 className="h-4 w-4" /> Volumen {config.volume}%
                            </Label>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={config.volume}
                                onChange={(e) => update('volume', parseInt(e.target.value, 10))}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setSoundOpen(false)} className="bg-white text-black hover:bg-white/90">
                            Cerrar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Presets dialog */}
            <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">Presets</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Input
                                value={presetName}
                                onChange={(e) => setPresetName(e.target.value)}
                                placeholder="Nombre del preset"
                                className="bg-[#0a0a0a] border-[#3a3a3f] text-white"
                            />
                            <Button onClick={savePreset} variant="outline" className="border-[#3a3a3f] text-white hover:bg-white/5">
                                <Save className="h-4 w-4" />
                            </Button>
                        </div>
                        {presets.length === 0 ? (
                            <p className="text-white/60 text-sm text-center py-4">No hay presets guardados.</p>
                        ) : (
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {presets.map((preset, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 rounded border border-[#3a3a3f]">
                                        <span className="text-white text-sm font-medium">{preset.name}</span>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => loadPreset(preset)} className="border-[#3a3a3f] text-white hover:bg-white/5">
                                                Cargar
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => deletePreset(idx)} className="border-[#3a3a3f] text-red-400 hover:bg-white/5">
                                                Borrar
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setPresetOpen(false)} className="bg-white text-black hover:bg-white/90">
                            Cerrar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
